import { type SelectionRange } from '../../machine/state/types.ts';
import { createEffect } from '../utils/create-effect.ts';

export interface ApplyFillEffectDeps {
  resolveRangeToIds: (range: SelectionRange) => {
    entryIds: string[];
    fieldIds: string[];
  };
  onFillValues: (params: {
    fillEntryIds: string[];
    fillFieldIds: string[];
    sourceEntryIds: string[];
    sourceFieldIds: string[];
  }) => void;
  onSelectFilledValues: (
    filledValues: Array<{ entryId: string; fieldId: string }>
  ) => void;
}

/**
 * Applies a completed fill operation by resolving the source and target ranges
 * to database IDs, invoking the injected mutation, then selecting the filled
 * values through the injected API callback on the next frame.
 *
 * The frame boundary prevents a re-entrant transition while `fillApplied` is
 * being delivered. This effect does not receive the machine or own fill gesture
 * state, and it subscribes only to `fillApplied`, so the follow-up selection
 * cannot loop back into this effect.
 */
export const createApplyFillEffect = createEffect(
  ({ resolveRangeToIds, onFillValues, onSelectFilledValues }: ApplyFillEffectDeps) => {
    return {
      deltaTypes: ['fillApplied'],

      onDelta(delta) {
        const source = resolveRangeToIds(delta.sourceRange);
        const fill = resolveRangeToIds(delta.fillRange);

        onFillValues({
          fillEntryIds: fill.entryIds,
          fillFieldIds: fill.fieldIds,
          sourceEntryIds: source.entryIds,
          sourceFieldIds: source.fieldIds
        });

        const filledValues = fill.entryIds.flatMap((entryId) =>
          fill.fieldIds.map((fieldId) => ({ entryId, fieldId }))
        );

        requestAnimationFrame(() => {
          onSelectFilledValues(filledValues);
        });
      }
    };
  }
);
